import React, { useState, useMemo } from 'react';
import useAPI from '../../hooks/useAPI';
import usePostInformasi from '../../hooks/usePostInformasi';
import useUpdateInformasi from '../../hooks/useUpdateInformasi';
import useDeleteInformasi from '../../hooks/useDeleteInformasi';
import endpoints from '../../Services/endpointAdmin';
import { addNotification } from '../../Services/notifikasi';
import {
  extractArrayPayload,
  findFieldDeep,
} from './adminScope';

function resolveInformasiId(item) {
  return (
    item?.id_informasi ||
    item?.id_informasi_mahasiswa ||
    item?.id ||
    findFieldDeep(item, ['id_informasi', 'id_informasi_mahasiswa', 'id'])
  );
}

function resolveTanggal(item) {
  // Cari field tanggal dari berbagai kemungkinan nama
  return (
    item?.tanggal ||
    item?.created_at ||
    item?.updated_at ||
    item?.tgl_dibuat ||
    item?.tgl_update ||
    item?.createdAt ||
    item?.updatedAt ||
    item?.created_date ||
    item?.update_date ||
    null
  );
}

function InformasiManager({ admin }) {
  // Fetch all informasi
  const { data: informasiData, loading, error } = useAPI(endpoints.informasi.getAll);

  // Hooks for mutations
  const { postInformasi, loading: postLoading } = usePostInformasi();
  const { putInformasi, loading: putLoading } = useUpdateInformasi();
  const { deleteInformasi, loading: deleteLoading } = useDeleteInformasi();
  const adminKodeUser = String(findFieldDeep(admin, ['kode_user', 'kodeUser']) || '').trim();
  const adminIdUser = String(findFieldDeep(admin, ['id_user', 'idUser', 'id']) || '').trim();

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [originalFoto, setOriginalFoto] = useState('');
  const [fotoMode, setFotoMode] = useState('url'); // 'url' atau 'upload'
  const [formData, setFormData] = useState({
    judul: '',
    deskripsi: '',
    isi: '',
    foto: '',
    tanggal: new Date().toISOString().split('T')[0],
  });

  const rawInformasiList = useMemo(() => {
    return extractArrayPayload(informasiData, [
      'informasi',
      'informasi_mahasiswa',
      'data_informasi',
      'rows',
    ]);
  }, [informasiData]);

  const informasiList = useMemo(() => rawInformasiList, [rawInformasiList]);

  const handleOpenForm = (berita = null) => {
    if (berita) {
      setEditingId(resolveInformasiId(berita));
      setOriginalFoto(berita.foto || '');
      setFotoMode('url'); // Default ke URL saat edit
      const tglDb = resolveTanggal(berita);
      setFormData({
        judul: berita.judul || '',
        deskripsi: berita.deskripsi || '',
        isi: berita.isi || '',
        foto: berita.foto || '',
        tanggal: tglDb ? tglDb.split('T')[0] : new Date().toISOString().split('T')[0],
      });
    } else {
      setEditingId(null);
      setOriginalFoto('');
      setFotoMode('url'); // Default ke URL saat create
      setFormData({
        judul: '',
        deskripsi: '',
        isi: '',
        foto: '',
        tanggal: new Date().toISOString().split('T')[0],
      });
    }
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditingId(null);
    setOriginalFoto('');
    setFotoMode('url');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 500KB)
    const maxSize = 500 * 1024; // 500KB
    if (file.size > maxSize) {
      addNotification({
        title: 'Ukuran File Terlalu Besar',
        message: `Ukuran foto tidak boleh melebihi 500KB. Ukuran file Anda: ${(file.size / 1024).toFixed(2)}KB`
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      addNotification({
        title: 'Tipe File Tidak Valid',
        message: 'Silakan upload file gambar (PNG, JPG, GIF, WebP)'
      });
      return;
    }

    // Compress image
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Resize if image is too large
      const maxWidth = 800;
      const maxHeight = 600;
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to base64 with reduced quality
      const base64String = canvas.toDataURL('image/jpeg', 0.7);
      setFormData(prev => ({
        ...prev,
        foto: base64String
      }));
    };
    img.onerror = () => {
      addNotification({
        title: 'Error',
        message: 'Gagal membaca file gambar'
      });
    };
    img.src = URL.createObjectURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.judul || !formData.deskripsi) {
      addNotification({
        title: 'Data Tidak Lengkap',
        message: 'Pastikan judul dan deskripsi diisi dengan benar.'
      });
      return;
    }

    if (!formData.foto) {
      addNotification({
        title: 'Foto Wajib Diisi',
        message: 'Foto berita wajib diisi lewat URL atau upload lokal sebelum menyimpan.'
      });
      return;
    }

    const currentTanggal = formData.tanggal || new Date().toISOString().split('T')[0];
    
    // Determine if foto was changed
    // For upload: must be base64 (starts with 'data:') and different from original
    // For URL: must be different from original URL
    const fotoChanged = formData.foto !== originalFoto;
    const fotoToSend = (editingId && !fotoChanged) ? undefined : (formData.foto || undefined);

    if (editingId) {
      // Update - set tanggal to now (last updated)
      const result = await putInformasi(
        editingId,
        formData.judul,
        formData.deskripsi,
        formData.isi,
        '', // kategori (empty)
        fotoToSend,
        currentTanggal,
        {
          ...(adminKodeUser ? { kode_user: adminKodeUser } : {}),
          ...(adminIdUser ? { id_user: adminIdUser } : {}),
        }
      );
      if (result) {
        addNotification({
          title: 'Sukses',
          message: 'Berita berhasil diperbarui'
        });
        handleCloseForm();
        window.location.reload();
      }
    } else {
      // Create
      const result = await postInformasi(
        formData.judul,
        formData.deskripsi,
        formData.isi,
        '', // kategori (empty)
        formData.foto || undefined,
        currentTanggal,
        {
          ...(adminKodeUser ? { kode_user: adminKodeUser } : {}),
          ...(adminIdUser ? { id_user: adminIdUser } : {}),
        }
      );
      if (result) {
        addNotification({
          title: 'Sukses',
          message: 'Berita berhasil ditambahkan'
        });
        handleCloseForm();
        window.location.reload();
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus berita ini?')) {
      const result = await deleteInformasi(id);
      if (result) {
        addNotification({
          title: 'Sukses',
          message: 'Berita berhasil dihapus'
        });
        window.location.reload();
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <p className="text-blue-600">Memuat data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50 py-10 px-6 lg:px-16">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent mb-2">
              Kelola Berita & Informasi
            </h1>
            <p className="text-slate-600 text-lg">Tambah, ubah, atau hapus berita yang ditampilkan untuk donatur</p>
          </div>
          <button
            onClick={() => handleOpenForm()}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200 whitespace-nowrap"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Tambah Berita
          </button>
        </div>

        {/* Berita Cards Grid */}
        {informasiList.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50/50 p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-blue-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 19c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H7z" />
            </svg>
            <p className="text-slate-600 text-lg font-semibold">Belum ada berita</p>
            <p className="text-slate-500 mt-2">Klik tombol "Tambah Berita" untuk membuat berita pertama Anda</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {informasiList.map((berita) => (
              <div
                key={resolveInformasiId(berita)}
                className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200 hover:border-blue-300 hover:scale-105 transform"
              >
                {/* Image Section */}
                {berita.foto && (
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300">
                    <img
                      src={berita.foto}
                      alt={berita.judul}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                )}

                {/* Content Section */}
                <div className="p-5 space-y-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {berita.judul}
                    </h3>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                      {berita.deskripsi || 'Tidak ada deskripsi'}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t border-slate-200">
                    <button
                      onClick={() => handleOpenForm(berita)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors border border-amber-200 hover:border-amber-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(resolveInformasiId(berita))}
                      disabled={deleteLoading}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors border border-red-200 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Hapus
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingId ? 'Edit Berita' : 'Tambah Berita Baru'}
              </h2>
              <button
                onClick={handleCloseForm}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Judul */}
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-2">Judul *</label>
                <input
                  type="text"
                  name="judul"
                  value={formData.judul}
                  onChange={handleChange}
                  placeholder="Masukkan judul berita"
                  className="w-full px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Tanggal */}
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-2">Tanggal *</label>
                <input
                  type="date"
                  name="tanggal"
                  value={formData.tanggal}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Pilih tanggal berita secara manual.</p>
              </div>

              {/* Deskripsi */}
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-2">Deskripsi *</label>
                <textarea
                  name="deskripsi"
                  value={formData.deskripsi}
                  onChange={handleChange}
                  placeholder="Masukkan deskripsi berita tanpa batasan kata atau kalimat"
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Deskripsi dapat diisi panjang sesuai kebutuhan.</p>
              </div>

              {/* Foto */}
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-2">Foto Berita</label>
                
                {/* Tab Buttons */}
                <div className="flex gap-2 mb-3 border-b-2 border-slate-200">
                  <button
                    type="button"
                    onClick={() => setFotoMode('url')}
                    className={`px-4 py-2 font-semibold text-sm transition-all ${
                      fotoMode === 'url'
                        ? 'text-blue-600 border-b-2 border-blue-600 -mb-2'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Dari Link URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setFotoMode('upload')}
                    className={`px-4 py-2 font-semibold text-sm transition-all ${
                      fotoMode === 'upload'
                        ? 'text-blue-600 border-b-2 border-blue-600 -mb-2'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Upload Lokal
                  </button>
                </div>

                <div className="space-y-3">
                  {/* URL Mode */}
                  {fotoMode === 'url' && (
                    <div>
                      <input
                        type="url"
                        value={formData.foto}
                        onChange={(e) => setFormData(prev => ({ ...prev, foto: e.target.value }))}
                        placeholder="https://example.com/foto.jpg"
                        required={fotoMode === 'url'}
                        title="URL foto berita wajib diisi."
                        onInvalid={(event) => event.target.setCustomValidity('URL foto berita wajib diisi.')}
                        onInput={(event) => event.target.setCustomValidity('')}
                        className="w-full px-4 py-2 rounded-lg border-2 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">Wajib isi URL lengkap foto dari internet.</p>
                    </div>
                  )}

                  {/* Upload Mode */}
                  {fotoMode === 'upload' && (
                    <div className="relative border-2 border-dashed border-blue-300 rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        required={fotoMode === 'upload' && !formData.foto}
                        title="Foto berita wajib diupload."
                        onInvalid={(event) => event.target.setCustomValidity('Foto berita wajib diupload.')}
                        onInput={(event) => event.target.setCustomValidity('')}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <div className="text-center pointer-events-none">
                        <svg className="w-8 h-8 mx-auto text-blue-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm font-semibold text-slate-700">Klik atau seret foto di sini</p>
                        <p className="text-xs text-slate-500 mt-1">Wajib upload PNG, JPG, GIF, atau WebP (maks 500KB).</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Preview */}
                  {formData.foto && (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 mb-2">Preview Foto:</p>
                      <div className="rounded-lg overflow-hidden border-2 border-slate-300">
                        <img src={formData.foto} alt="Preview" className="w-full h-40 object-cover" />
                      </div>
                    </div>
                  )}
                  
                  {/* Clear Button */}
                  {formData.foto && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, foto: '' }))}
                      className="w-full px-3 py-2 text-sm font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      Hapus Foto
                    </button>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="flex-1 px-4 py-2 rounded-lg border-2 border-slate-300 text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={postLoading || putLoading}
                  className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {postLoading || putLoading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Memproses...</span>
                    </>
                  ) : (
                    <span>{editingId ? 'Update Berita' : 'Tambah Berita'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InformasiManager;
